import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit"
import { clientMutation, clientQuery } from '../../../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../../../core/defaultValues';
import { resetALL } from '../../../../../../../.globals/redux/actions';

//STATE INITIAL
const INIT_STATE = {
    data: [],
    loading: false,
    error: null,
    updateRenderData: false,
    lastPatch: null,
    filters: {
        openSearch: "",
        dateRange: {},
        events: [],
        hasNotes: null,
        starred: false,
        mediaDeleted: false,
        mediaType: "All",
        reviewStatus: null,
        limit: 20,
        nextToken: null,
        timeFrame: "today"
        //,
        // entity: null,
        // entities: [{
        //     "id": -1,
        //     "name": "All"
        // }],
        // eventsType: [],
    },
};

import { formatAvailableMedia } from 'Modules/video/.globals/helpers';

export const mergeEventsById = (items, updates = []) => {
    const merged = [...items];
    updates.forEach((element) => {
        const idx = merged.findIndex((event) => event?.id === element?.id);
        if (idx > -1) merged[idx] = { ...merged[idx], ...element };
    });
    return merged;
};

export const removeEventsByIds = (items, idsToRemove = []) => {
    const remaining = [...items];
    idsToRemove.forEach((id) => {
        const idx = remaining.findIndex((event) => event.id === id);
        if (idx > -1) remaining.splice(idx, 1);
    });
    return remaining;
};

export const query = `
query searchVideoEvents (
    $openSearch: String, 
    $entities: [EntityInput]!, 
    $dateRange: AWSDateTimeRange!, 
    $eventNames: [String], 
    $hasNotes: Boolean, 
    $starred: Boolean, 
    $mediaDeleted: Boolean,
    $mediaType: enumMediaType, 
    $reviewStatus: enumVideoReviewStatus, 
    $limit: Int, 
    $nextToken: String) 
{
        data: searchVideoEvents (openSearch: $openSearch, entities: $entities, dateRange: $dateRange, eventNames: $eventNames, hasNotes: $hasNotes, starred: $starred, mediaDeleted: $mediaDeleted, mediaType: $mediaType, reviewStatus: $reviewStatus, limit: $limit, nextToken: $nextToken) {
            items {
                reading {
                    id
                    deviceId
                    heading
                    eventCode
                    eventName
                    driverId
                    driverName
                    unitTime
                    deviceData
                    starred
                    notes
                    speed
                    normalizedSpeed
                    ecuSpeed
                    unitType
                    formattedAddress
                    speedLimit
                }
                unit {
                    id
                    label
                    type
                    hid
                    esn
                    imei
                    driverId
                    driverName
                    notes
                    useEcuSpeed
                    timeZone {
                    id
                    name
                    }
                    tags {
                    id
                    label
                    color
                    }
                }
                linkedDevice {
                id
                esn
                deviceModelId
                deviceModelName
                manufacturerId
                manufacturerName
                hasVideo
                }
                reviewStatus {
                    jsonValue
                }
            }
            nextToken
        }
    }
`;

export const searchVideoEvents = createAsyncThunk('videoEventsList/list/search', async ({ args
}, { rejectWithValue, dispatch, getState }) => {
    try {
        const {
            limit,
            dateRange,
            openSearch,
            resetSearch,
            starred,
            entities,
            hideTableLoading,
            events,
            mediaDeleted,
            mediaType,
            reviewStatus,
            hasNotes,
            nextToken
        } = args
        
        const result = await clientQuery(
            query,
            {
                limit,
                dateRange,
                openSearch,
                resetSearch,
                nextToken,
                starred,
                entities: entities || [{ name: 'All', id: -1 }],
                hideTableLoading,
                eventNames: events,
                mediaDeleted,
                mediaType,
                reviewStatus,
                hasNotes
            },
            endpoints.GRAPHQL_GENERAL_V2
        )

        return { items: result?.data?.items && formatAvailableMedia(result?.data?.items) || [], nextToken: result?.data?.nextToken || null };
    } catch (exc) {
        console.log(exc);
        ExceptionManager(exc, route, "searchVideoEvents");
        return rejectWithValue(exc)
    }
}, {
    condition: (payload, { getState }) => {
        const { videoEventsListRedux } = getState();
        return !videoEventsListRedux?.loading;
    },
});


//Slice
const videoEventsListRedux = createSlice({
    name: 'videoEventsList',
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE }
        },
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload }
        },
        updateEventsData(state, action) {
            const events = action.payload?.events || [];
            const nextSeq = (state.lastPatch?.seq || 0) + 1;

            return {
                ...state,
                data: mergeEventsById(state.data, events),
                updateRenderData: true,
                lastPatch: { seq: nextSeq, type: "update", events },
            }
        },
        deleteEvent(state, action) {
            const eventIds = action.payload?.eventIds || [];
            const nextSeq = (state.lastPatch?.seq || 0) + 1;

            return {
                ...state,
                data: removeEventsByIds(state.data, eventIds),
                lastPatch: { seq: nextSeq, type: "delete", eventIds },
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        builder.addCase(searchVideoEvents.pending, (state, action) => {

            let filters = { ...state.filters, ...action.meta.arg.args }
            // Modificar la lógica para manejar el offset basado en unitTime
            const shouldResetData = filters.nextToken === null;
            return { loading: true, error: null, filters: filters, data: shouldResetData ? [] : state.data }
        });

        builder.addCase(searchVideoEvents.fulfilled, (state, action) => {

            let filters = action.meta.arg.args
            const { items, nextToken } = action.payload
            const seenIds = new Set(state.data.map((e) => e?.id).filter((id) => id != null))
            const merged = [...state.data]
            for (const item of items) {
                const id = item?.id
                if (id != null && seenIds.has(id)) continue
                if (id != null) seenIds.add(id)
                merged.push(item)
            }
            return { ...state, loading: false, data: merged, nextToken }
        });

        builder.addCase(searchVideoEvents.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

    },
})

export const { actions, reducer } = videoEventsListRedux;

export const { reset, setFilters, updateEventsData, deleteEvent } = actions;
export default reducer;